import { useCallback, useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { useFormikContext } from 'formik';
import { UnsavedChangesDialog } from '@/components/dialog/unsavedChanges/UnsavedChangesDialog';
import { useConfigDataSource } from '@/contexts/ConfigDataSourceContext';
import { ResponseError } from '@/components/responseError/ResponseError';

export type DataSourceNavigationButtonsProps = {
  handlePreviousStep: () => void;
  handleNextStep: () => void;
  handleReview: () => void;
  goToPreviousStep: () => void;
  disabled: boolean;
  loadingNext?: boolean;
  loadingReview?: boolean;
  isReviewButtonVisible?: boolean;
  'data-testid'?: string;
};

export const DataSourceNavigationButtons = ({
  disabled,
  handlePreviousStep,
  handleNextStep,
  handleReview,
  goToPreviousStep,
  loadingNext,
  loadingReview,
  isReviewButtonVisible = true,
  'data-testid': dataTestId = 'datasource-navigation-buttons',
}: DataSourceNavigationButtonsProps) => {
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] =
    useState(false);
  const { dirty, handleSubmit, isSubmitting } = useFormikContext();
  const { responseError } = useConfigDataSource();

  const onPreviousStep = useCallback(() => {
    if (!dirty) {
      goToPreviousStep();
    } else {
      setUnsavedChangesDialogOpen(true);
    }
  }, [dirty, goToPreviousStep]);

  const onConfirm = useCallback(() => {
    handlePreviousStep();
    handleSubmit();
    setUnsavedChangesDialogOpen(false);
  }, [handleSubmit, handlePreviousStep]);

  return (
    <>
      {responseError && (
        <ResponseError
          error={responseError}
          data-testid="datasource-response-error"
        />
      )}
      <div
        className="flex justify-between"
        data-testid="datasource-navigation-container"
      >
        <div>
          <Button
            type="button"
            variant="default"
            disabled={disabled}
            leftIcon="lucide:chevron-left"
            onClick={() => onPreviousStep()}
            data-testid={`${dataTestId}-back-button`}
          >
            Back
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            type="submit"
            variant="default"
            disabled={disabled}
            rightIcon="lucide:chevron-right"
            onClick={() => handleNextStep()}
            loading={loadingNext}
            data-testid={`${dataTestId}-next-button`}
          >
            Next
          </Button>
          {isReviewButtonVisible && (
            <Button
              variant="primary"
              type="submit"
              rightIcon="lucide:chevrons-right"
              onClick={() => handleReview()}
              loading={loadingReview}
              data-testid={`${dataTestId}-review-button`}
            >
              Review
            </Button>
          )}
        </div>
      </div>
      <UnsavedChangesDialog
        open={unsavedChangesDialogOpen}
        onDiscard={() => goToPreviousStep()}
        onCancel={() => setUnsavedChangesDialogOpen(false)}
        onConfirm={() => onConfirm()}
        isConfirming={isSubmitting}
        data-testid={`${dataTestId}-unsaved-changes-dialog`}
      />
    </>
  );
};
